import { $Types } from '@Ravnur-Inc/core/typings/tsx';
import { Prop } from '@Ravnur-Inc/decorators';
import isEqual from '@Ravnur-Inc/nanoutils/isEqual';
import { createVNode } from 'vue';
import { Vue, Options } from 'vue-class-component';

import { EntityRouteKey } from '@/entity-route-config';
import { scrollToFirstErrorInDOM } from '@/helpers/dom-errors';
import { BaseEditorComponent } from '@/types/Component';
import { IRepository } from '@/types/Repository';
import { fixQuillShit, clearHiddenCharacters } from '@Ravnur-Inc/helpers/fix-quill-shit';

import './edit-entity-component.scss';

type Props<E extends Entity> = {
  repository: IRepository<E>;
  originEntity: Entity;
  editor: new () => BaseEditorComponent<E>;
};

type Emits<E extends Entity> = {
  onRefresh: (entity: E) => void;
  onStored: (entity: E) => void;
};

const CN = 'edit-entity-component';

@Options({
  emits: ['refresh', 'to-list', 'stored'],
  watch: {
    originEntity(this: EditEntityComponent<Entity>) {
      this.handleOriginEntityChanged();
    },
  },
})
export default class EditEntityComponent<E extends Entity> extends Vue {
  declare $props: $Types<Props<E>, Emits<E>>;
  declare $refs: {
    editor: BaseEditorComponent<E>;
  };

  @Prop({}) repository: IRepository<E>;
  @Prop({}) originEntity: E;
  @Prop({}) editor: new () => BaseEditorComponent<E>;

  private saving = false;
  private localEntity: Partial<E> = {};
  private unwatch: Nullable<() => void> = null;

  private get entity(): E {
    const { originEntity, localEntity } = this;
    return { ...originEntity, ...localEntity } as E;
  }

  public get isChanged(): boolean {
    const { originClone, localClone } = fixQuillShit(this.originEntity, this.entity);

    // Hack, because some may be proxy
    const current = JSON.parse(JSON.stringify(localClone));
    const origin = JSON.parse(JSON.stringify(originClone));

    if (current.description) {
      current.description = clearHiddenCharacters(current.description);
    }

    if (origin.description) {
      origin.description = clearHiddenCharacters(origin.description);
    }

    return !isEqual(origin, current);
  }

  public get isInvalid(): boolean {
    const { editor } = this.$refs;

    return editor.v$.$invalid;
  }

  public hasUnsavedData() {
    return this.isChanged;
  }

  protected handleOriginEntityChanged() {
    this.$refs.editor.refresh(this.originEntity);
  }

  mounted() {
    this.unwatch = this.$refs.editor.$watch('local', this.handleLocalEntityChanged);
    this.handleOriginEntityChanged();
  }

  beforeUnmount() {
    this.unwatch?.();
  }

  render() {
    return (
      <div class={CN}>
        <div class={`${CN}__container`}>
          {createVNode(this.editor, {
            ref: 'editor',
            origin: this.originEntity,
          })}
        </div>

        <div class={`${CN}__actions`} v-show={!!this.originEntity}>
          <r-button
            class={[`${CN}__btn`, `${CN}__btn--cancel`]}
            mode="borderless"
            onclick={this.toList}
          >
            <l10n group="common" tkey="action__cancel" />
          </r-button>

          <r-button
            class={`${CN}__btn`}
            color="primary"
            data-testid="entity-save-btn"
            disabled={!this.isChanged || this.isInvalid}
            loading={this.saving}
            onclick={this.save}
          >
            <l10n group="common" tkey="action__save" />
          </r-button>
        </div>
      </div>
    );
  }

  private toList() {
    if (this.originEntity) {
      this.$emit('refresh', this.originEntity);
    }
    this.$nextTick(() => {
      this.$moveToPageView(EntityRouteKey.LIST, { canceling: 'true' });
    });
  }

  private async save() {
    const { editor } = this.$refs;

    if (!(await editor.v$.$validate?.())) {
      this.$nextTick(this.scrollToInvalidElement);
      return;
    }

    this.saving = true;

    try {
      await this.repository.save(this.entity);
      if (editor.pushAdditionalData) {
        await editor.pushAdditionalData();
        const e = await this.repository.get(this.originEntity.id);
        this.$emit('stored', e);
      } else {
        this.$emit('stored', this.entity);
      }
    } catch (e) {
      this.$processException(e);
    }

    this.saving = false;
  }

  private handleLocalEntityChanged(local: Partial<E>) {
    this.localEntity = local;
  }

  private scrollToInvalidElement() {
    const { editor } = this.$refs;
    if (editor.scrollToInvalidElement instanceof Function) {
      editor.scrollToInvalidElement();
    } else {
      scrollToFirstErrorInDOM(this.$el);
    }
  }
}
